import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BrainCircuitIcon } from "lucide-react";

import { useDetections } from "@/api/sinsight";
import { useCameras } from "@/api/cameras";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { Timestamp } from "@/components/ui/timestamp";
import { DeviceContextLabel } from "@/components/DeviceContextLabel";
import { shortId, type CameraRef } from "@/types/deviceRef";

function locationBadge(loc: string) {
  if (loc === "edge") {
    return (
      <Badge variant="online">Edge</Badge>
    );
  }
  return <Badge variant="warning">Server</Badge>;
}

const TYPE_LABELS: Record<string, string> = {
  after_hours_intrusion: "After-hours intrusion",
  zone_intrusion: "Zone intrusion",
  line_crossing: "Line crossing",
  crowd_threshold: "Crowd threshold",
  camera_tamper: "Camera tamper",
  loitering: "Loitering",
  fire_confirmed: "🔥 Fire confirmed",
  smoke: "Smoke (assistive)",
  flame: "Flame (assistive)",
};

function typeBadge(type: string) {
  // S16-fire — a fault/impairment STATUS, shown DISTINCTLY (not a threat).
  if (type === "fire_assist_fault") {
    return <Badge variant="warning">Fire-assist fault</Badge>;
  }
  // S16 / S16-batch / S16-fire — alert types flagged loudly.
  if (type in TYPE_LABELS) {
    return <Badge variant="error">{TYPE_LABELS[type]}</Badge>;
  }
  if (type === "person") {
    return <Badge variant="secondary">Person</Badge>;
  }
  return <Badge>{type}</Badge>;
}

const FIRE_TYPES = new Set(["smoke", "flame", "fire_confirmed", "fire_assist_fault"]);

// S16-fire — surface the cross-verify audit so operators can judge.
function fireDetail(extra: Record<string, unknown>): string {
  const parts: string[] = [];
  if (extra.escalation_level) parts.push(String(extra.escalation_level));
  if (extra.panel_state) {
    parts.push(`panel=${extra.panel_state}${extra.panel_state_fresh ? "" : " (stale)"}`);
  }
  const rc = (extra.reason_codes as string[] | undefined) ?? [];
  if (rc.length) parts.push(rc.join(","));
  return parts.join(" · ");
}

// Pull the most relevant detail out of a detection's extra per type.
function typeDetail(type: string, extra: Record<string, unknown>): string {
  if (type === "zone_intrusion") {
    const zones = (extra.zones as { name: string }[] | undefined) ?? [];
    return zones.map((z) => z.name).join(", ");
  }
  if (type === "line_crossing") {
    const lines = (extra.lines as { name: string; side: string }[] | undefined) ?? [];
    return lines.map((l) => `${l.name} (${l.side})`).join(", ");
  }
  if (type === "crowd_threshold") {
    return `${extra.count ?? "?"} ≥ ${extra.threshold ?? "?"}`;
  }
  if (type === "camera_tamper") {
    const sig = (extra.signals as { signal: string }[] | undefined) ?? [];
    return sig.map((s) => s.signal).join(", ");
  }
  if (type === "loitering") {
    return `${extra.dwell_seconds ?? "?"}s dwell`;
  }
  if (FIRE_TYPES.has(type)) {
    return fireDetail(extra);
  }
  return "";
}

const PAGE_SIZE = 25;
const TYPE_OPTIONS = [
  "person",
  ...Object.keys(TYPE_LABELS),
];

export default function DetectionsPage() {
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");

  const { data, isLoading, isError, refetch } = useDetections({
    detection_type: type || undefined,
    since: since || undefined,
    until: until || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  // Batch 1 (UX/IA) — resolve camera id → name + NVR context (client join),
  // so a detection shows which camera/NVR it came from, not a truncated UUID.
  const cameras = useCameras({ limit: 200 });
  const cameraById = useMemo(() => {
    const m = new Map<string, CameraRef>();
    for (const c of cameras.data?.items ?? []) m.set(c.id, c);
    return m;
  }, [cameras.data]);

  const anyFilter = type !== "" || since !== "" || until !== "";
  const clear = () => {
    setType("");
    setSince("");
    setUntil("");
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <BrainCircuitIcon className="h-6 w-6" />
          Analytics — Detections
        </h1>
        <p className="text-sm text-muted-foreground">
          SInsight detection feed. Edge-scored and server-scored
          events converge on the same store; the location badge
          shows where the detection was produced.
        </p>
      </div>

      {/* Batch 1 — filters + paging so older detections are reachable (the
          feed was previously capped at the latest 50 with no way back). */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Type</label>
          <select
            value={type}
            onChange={(e) => { setType(e.target.value); setPage(1); }}
            className="block h-8 rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="">All types</option>
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">From</label>
          <input
            type="datetime-local"
            value={since}
            onChange={(e) => { setSince(e.target.value); setPage(1); }}
            className="block h-8 rounded-md border border-input bg-background px-2 text-xs"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">To</label>
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => { setUntil(e.target.value); setPage(1); }}
            className="block h-8 rounded-md border border-input bg-background px-2 text-xs"
          />
        </div>
        {anyFilter && (
          <Button variant="ghost" size="sm" className="h-8" onClick={clear}>
            Clear
          </Button>
        )}
        {data && (
          <span className="ml-auto text-xs text-muted-foreground">
            {data.total} detection{data.total === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {isLoading && <p className="text-sm">Loading…</p>}
      {/* An empty detections list reads as "nothing was detected" — a finding
          about the site. A failed fetch must never render as that. */}
      {isError && (
        <p className="text-sm text-destructive">
          Could not load detections.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="underline underline-offset-2"
          >
            Retry
          </button>
        </p>
      )}

      {data && data.items.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {anyFilter ? "No detections match these filters." : "No detections yet."}
          </CardContent>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Camera (Site › NVR › Ch)</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Detail</th>
                  <th className="px-3 py-2 text-left">Confidence</th>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-left">Snapshot</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => {
                  const cam = cameraById.get(row.camera_id);
                  return (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                      <Timestamp value={(row.occurred_at ?? row.created_at) as never} />
                    </td>
                    <td className="px-3 py-2">
                      {cam ? (
                        <div className="space-y-0.5">
                          <Link
                            to={`/cameras/${cam.id}`}
                            className="text-sm font-medium hover:underline"
                          >
                            {cam.name ?? "Camera"}
                          </Link>
                          <DeviceContextLabel camera={cam} variant="compact" className="max-w-[16rem]" />
                        </div>
                      ) : (
                        <span className="font-mono text-xs text-muted-foreground">
                          {shortId(row.camera_id)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {typeBadge(row.detection_type)}
                      {FIRE_TYPES.has(row.detection_type)
                        && (row.extra as Record<string, unknown>)
                          ?.validation_status === "beta_unvalidated" && (
                        <Badge variant="warning" className="ml-1">BETA</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {typeDetail(
                        row.detection_type,
                        (row.extra as Record<string, unknown>) ?? {},
                      ) || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {(row.confidence * 100).toFixed(0)}%
                    </td>
                    <td className="px-3 py-2">
                      {locationBadge(row.inference_location)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.snapshot_ref ? (
                        <span className="font-mono">
                          {row.snapshot_ref.slice(0, 24)}…
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <Badge variant={
                        row.status === "emitted"
                          ? "online"
                          : row.status === "failed"
                            ? "error"
                            : "warning"
                      }>{row.status}</Badge>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t">
              <Pagination
                total={data.total}
                page={page}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
